import React, { PropsWithChildren } from "react";
//#region types
import { WizardSectionProps } from "models/propTypes/wintFormPropsTypes";
import { IconDefinitionConstants } from "utils/constants";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import { Accordion, AccordionDetails, AccordionSummary, CircularProgress, Grid } from "@mui/material";
import Icon from "../../icon";
import useWizardStepContext from "./wizardStep/useContext";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";
import { LocalizationHelperInstance } from "utils/helpers";

const WizardSection: React.FC<PropsWithChildren<WizardSectionProps>> = (props) => {
  const { header, description, hasErrors, submitted, children, isContentLoading, testId } = props;

  const { currentStep, currentStepExpanded, visitedSteps, goToStep } = props.wizardProps!;
  const { index } = useWizardStepContext();

  const isExpanded = currentStep === index && currentStepExpanded;

  const onStepClick = () => {
    goToStep(index);
  };

  return (
    <Accordion
      className={cssExports.wizardStep}
      expanded={isExpanded}
      classes={{
        expanded: cssExports.expandedStep,
      }}
      data-testid={testId}
    >
      <AccordionSummary
        classes={{
          root: clsx(
            cssExports.wizardStepHeader,
            index === currentStep && currentStepExpanded
              ? cssExports.currentStep
              : !hasErrors
                ? cssExports.completeStep
                : (submitted || visitedSteps?.has(index)) && hasErrors
                  ? cssExports.errorStep
                  : ""
          ),
          content: cssExports.titleContent,
          expanded: cssExports.expanded,
          expandIconWrapper: cssExports.expandIcon,
        }}
        expandIcon={<></>}
        onClick={onStepClick}
      >
        <Grid container direction="row" wrap="nowrap" justifyContent="space-between">
          <Grid item xs className={cssExports.wizardStepHeaderTitleContentWrapper}>
            <Grid container item alignItems="center" spacing={2}>
              <Grid item>
                <Grid item className={clsx(cssExports.wizardStepNumber)}>
                  {isContentLoading ? (
                    <CircularProgress size={16} thickness={6} className={cssExports.loadingIcon} />
                  ) : !hasErrors && index !== currentStep ? (
                    <Icon icon={IconDefinitionConstants.WValidateCheck} />
                  ) : (
                    <span>{index + 1}</span>
                  )}
                </Grid>
              </Grid>
              <Grid
                item
                xs
                container
                direction={"column"}
                justifyContent={"center"}
                className={cssExports.wizardStepHeaderTitleContainer}
              >
                <Grid item container className={cssExports.wizardStepHeaderTitle}>
                  {LocalizationHelperInstance.getFormattedMessage(header)}
                </Grid>
                <Grid item container className={cssExports.wizardStepHeaderSubTitle}>
                  <span>{description ? LocalizationHelperInstance.getFormattedMessage(description) : ""}</span>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            {index === currentStep && currentStepExpanded ? (
              ""
            ) : visitedSteps?.has(index) && hasErrors ? (
              <span className={cssExports.wizardStepErrorText}>
                {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.SomethingIsMissingReviewAgain)}
              </span>
            ) : !hasErrors ? (
              <span className={cssExports.wizardStepDoneText}>
                {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Done)}
              </span>
            ) : (
              ""
            )}
          </Grid>
        </Grid>
      </AccordionSummary>
      <AccordionDetails
        classes={{
          root: cssExports.wizardStepContent,
        }}
      >
        {currentStep === index && currentStepExpanded ? <Grid container>{children} </Grid> : <></>}
      </AccordionDetails>
    </Accordion>
  );
};

export default WizardSection;
